import { supabase } from './supabase'
import type { User } from '@/types/cat'

export class AuthError extends Error {
  constructor(message: string, public code?: string) {
    super(message)
    this.name = 'AuthError'
  }
}

export const auth = {
  /**
   * Get current authenticated user
   */
  getCurrentUser: async (): Promise<User | null> => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser()
      
      if (error) throw new AuthError(error.message, (error as any).code || error.name)
      if (!user) return null
      
      return {
        id: user.id,
        email: user.email!,
        name: user.user_metadata?.full_name || user.user_metadata?.name,
        avatar_url: user.user_metadata?.avatar_url,
        role: user.user_metadata?.role || 'translator',
        created_at: user.created_at
      }
    } catch (error) {
      console.error('Error getting current user:', error)
      return null
    }
  },

  /**
   * Sign in with email and password
   */
  signIn: async (email: string, password: string): Promise<User> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password
      })

      if (error) throw new AuthError(error.message, (error as any).code || error.name)
      if (!data.user) throw new AuthError('No user returned from sign in')

      return {
        id: data.user.id,
        email: data.user.email!,
        name: data.user.user_metadata?.full_name || data.user.user_metadata?.name,
        avatar_url: data.user.user_metadata?.avatar_url,
        role: data.user.user_metadata?.role || 'translator',
        created_at: data.user.created_at
      }
    } catch (error) {
      if (error instanceof AuthError) throw error
      throw new AuthError('Failed to sign in')
    }
  },

  /**
   * Sign up new user
   */
  signUp: async (email: string, password: string, metadata?: {
    full_name?: string
    role?: string
  }): Promise<User> => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: {
            full_name: metadata?.full_name || '',
            role: metadata?.role || 'translator'
          }
        }
      })

      if (error) throw new AuthError(error.message, (error as any).code || error.name)
      if (!data.user) throw new AuthError('No user returned from sign up')
      // With email confirmation on, Supabase hides an existing account by
      // returning a user with no identities instead of an error.
      if (Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        throw new AuthError('User already registered', 'user_already_exists')
      }

      return {
        id: data.user.id,
        email: data.user.email!,
        name: metadata?.full_name,
        avatar_url: undefined,
        role: (metadata?.role as any) || 'translator',
        created_at: data.user.created_at
      }
    } catch (error) {
      if (error instanceof AuthError) throw error
      throw new AuthError('Failed to create account')
    }
  },

  /**
   * Sign out current user
   */
  signOut: async (): Promise<void> => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw new AuthError(error.message, (error as any).code || error.name)
    } catch (error) {
      if (error instanceof AuthError) throw error
      throw new AuthError('Failed to sign out')
    }
  },

  /**
   * Reset password
   */
  resetPassword: async (email: string): Promise<void> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        {
          redirectTo: `${window.location.origin}/reset-password`
        }
      )
      
      if (error) throw new AuthError(error.message, (error as any).code || error.name)
    } catch (error) {
      if (error instanceof AuthError) throw error
      throw new AuthError('Failed to send reset email')
    }
  },

  /**
   * Update password
   */
  updatePassword: async (newPassword: string): Promise<void> => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })
      
      if (error) throw new AuthError(error.message, (error as any).code || error.name)
    } catch (error) {
      if (error instanceof AuthError) throw error
      throw new AuthError('Failed to update password')
    }
  },

  /**
   * Update user profile
   */
  updateProfile: async (updates: {
    full_name?: string
    avatar_url?: string
  }): Promise<User> => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: updates
      })
      
      if (error) throw new AuthError(error.message, (error as any).code || error.name)
      if (!data.user) throw new AuthError('No user returned from update')

      return {
        id: data.user.id,
        email: data.user.email!,
        name: data.user.user_metadata?.full_name || data.user.user_metadata?.name,
        avatar_url: data.user.user_metadata?.avatar_url,
        role: data.user.user_metadata?.role || 'translator',
        created_at: data.user.created_at
      }
    } catch (error) {
      if (error instanceof AuthError) throw error
      throw new AuthError('Failed to update profile')
    }
  },

  /**
   * Listen to auth state changes
   */
  onAuthStateChange: (callback: (user: User | null) => void) => {
    return supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        const user: User = {
          id: session.user.id,
          email: session.user.email!,
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name,
          avatar_url: session.user.user_metadata?.avatar_url,
          role: session.user.user_metadata?.role || 'translator',
          created_at: session.user.created_at
        }
        callback(user)
      } else {
        callback(null)
      }
    })
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: async (): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      return !!session
    } catch {
      return false
    }
  },

  /**
   * Get current session
   */
  getSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession()
      if (error) throw new AuthError(error.message, (error as any).code || error.name)
      return session
    } catch (error) {
      if (error instanceof AuthError) throw error
      throw new AuthError('Failed to get session')
    }
  }
}

export default auth
